import React, { useState, useEffect } from 'react';
import { SystemBackupLog } from '../../data/initialContactsData';
import {
  getBackupLogs,
  triggerAutoBackup,
  exportFullDatabaseJson,
  restoreFullDatabaseFromJson,
  restoreSnapshotById,
  getContacts,
  getMessages,
  getTasks,
} from '../../utils/portalStorage';

export const BackupManagement: React.FC = () => {
  const [logs, setLogs] = useState<SystemBackupLog[]>([]);
  const [restoreStatus, setRestoreStatus] = useState<{ success: boolean; message: string } | null>(null);

  const contacts = getContacts();
  const messages = getMessages();
  const tasks = getTasks();

  const loadLogs = () => {
    setLogs(getBackupLogs());
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const handleManualBackup = () => {
    triggerAutoBackup('Manual User Triggered');
    loadLogs();
    setRestoreStatus({
      success: true,
      message: '⚡ Manual PostgreSQL Snapshot created and saved successfully!',
    });
  };

  const handleRestoreSnapshot = (log: SystemBackupLog) => {
    if (confirm(`Are you sure you want to restore the system state to the snapshot taken on ${new Date(log.timestamp).toLocaleString()}?`)) {
      const res = restoreSnapshotById(log.id);
      setRestoreStatus(res);
      loadLogs();
    }
  };

  const handleRestoreFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        if (text) {
          const res = restoreFullDatabaseFromJson(text);
          setRestoreStatus(res);
          loadLogs();
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="space-y-6 text-white">
      {/* Header Banner */}
      <div className="bg-slate-900 border border-slate-800 p-5 rounded-2xl shadow-xl flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-black font-serif text-amber-300 uppercase tracking-wide">
              Database Persistence & Automated Backup Engine
            </h2>
            <span className="bg-emerald-900 text-emerald-300 text-[10px] px-2.5 py-0.5 rounded-full font-mono font-bold border border-emerald-600/50">
              ● Database Active & Syncing
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Real-time local file-backed persistence with automatic periodic snapshot creation & full JSON backups
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleManualBackup}
            className="bg-emerald-700 hover:bg-emerald-600 text-white font-extrabold text-xs px-4 py-2 rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
          >
            <span>⚡</span> Trigger Instant Snapshot
          </button>

          <button
            onClick={exportFullDatabaseJson}
            className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-xs px-4 py-2 rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
          >
            <span>📥</span> Export Full DB (.json)
          </button>
        </div>
      </div>

      {/* System Record Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-xl space-y-1">
          <span className="text-[10px] uppercase font-mono text-slate-400 font-bold">Total Contacts Record</span>
          <div className="text-2xl font-black text-amber-300 font-serif">{contacts.length}</div>
          <p className="text-[11px] text-slate-500">Global Uzairue Directory entries</p>
        </div>

        <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-xl space-y-1">
          <span className="text-[10px] uppercase font-mono text-slate-400 font-bold">Total Messages Volume</span>
          <div className="text-2xl font-black text-emerald-400 font-serif">{messages.length}</div>
          <p className="text-[11px] text-slate-500">Across 6 Channel & Direct threads</p>
        </div>

        <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-xl space-y-1">
          <span className="text-[10px] uppercase font-mono text-slate-400 font-bold">Active Collaboration Tasks</span>
          <div className="text-2xl font-black text-amber-400 font-serif">{tasks.length}</div>
          <p className="text-[11px] text-slate-500">Palace logistics & preparations</p>
        </div>
      </div>

      {/* Backup Upload Restore Banner */}
      <div className="bg-slate-950 border border-slate-800 p-5 rounded-2xl space-y-3">
        <h3 className="font-serif text-amber-300 font-bold text-xs uppercase tracking-wider flex items-center gap-2">
          <span>🔄</span> Restore Database from Backup File
        </h3>
        <p className="text-xs text-slate-300">
          Upload a previously downloaded <code>.json</code> database snapshot to restore contacts, messaging threads, and task registers.
        </p>

        <div className="flex items-center gap-3">
          <input
            type="file"
            accept=".json"
            onChange={handleRestoreFile}
            className="text-xs text-slate-400 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-slate-800 file:text-amber-300 hover:file:bg-slate-700 cursor-pointer"
          />
        </div>

        {restoreStatus && (
          <div
            className={`p-3 rounded-xl text-xs font-bold ${
              restoreStatus.success
                ? 'bg-emerald-950 text-emerald-300 border border-emerald-600'
                : 'bg-red-950 text-red-300 border border-red-700'
            }`}
          >
            {restoreStatus.message}
          </div>
        )}
      </div>

      {/* Backup Logs History Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl space-y-3 p-5">
        <h3 className="font-serif text-amber-300 font-bold text-xs uppercase tracking-wider flex items-center gap-2">
          <span>📜</span> System Snapshot & Backup Execution Log
        </h3>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-slate-200">
            <thead className="bg-slate-950 text-slate-400 uppercase font-mono text-[10px] border-b border-slate-800">
              <tr>
                <th className="p-3">Timestamp</th>
                <th className="p-3">Type</th>
                <th className="p-3">Contacts</th>
                <th className="p-3">Messages</th>
                <th className="p-3">Tasks</th>
                <th className="p-3">Dump Size</th>
                <th className="p-3">Status</th>
                <th className="p-3 text-right">Point-in-Time Restore</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 font-mono text-[11px]">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-slate-800/50">
                  <td className="p-3 text-white font-bold">{new Date(log.timestamp).toLocaleString()}</td>
                  <td className="p-3 text-amber-300">{log.backupType}</td>
                  <td className="p-3">{log.contactsCount}</td>
                  <td className="p-3">{log.messagesCount}</td>
                  <td className="p-3">{log.tasksCount}</td>
                  <td className="p-3 text-slate-400">{(log.fileSizeBytes / 1024).toFixed(1)} KB</td>
                  <td className="p-3">
                    <span className="bg-emerald-950 text-emerald-300 px-2 py-0.5 rounded border border-emerald-800 font-bold text-[10px]">
                      ✓ {log.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      onClick={() => handleRestoreSnapshot(log)}
                      className="px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-slate-950 text-[10px] font-black uppercase rounded-lg transition-all cursor-pointer shadow"
                      title="One-click restore system state to this snapshot"
                    >
                      🔄 Restore State
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BackupManagement;
